import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { sendMessagesToStudents } from 'api/all'

const PLACEHOLDER_STUDENT = '{{student_name}}'
const PLACEHOLDER_PARENT = '{{parent_name}}'
const MAX_VISIBLE = 3

export const useSendMessageModal = ({
	isOpen,
	onClose,
	selectedStudents,
	selectedParents = [],
	initialMessage = '',
	customSend = null,
	successToastMessage = 'Message sent successfully!',
	onAfterSendSuccess = null,
}) => {
	const cursorOffsetRef = useRef(null)
	const messageEditorRef = useRef(null)
	const [localStudents, setLocalStudents] = useState([])
	const [addParents, setAddParents] = useState(false)
	const [showAllStudents, setShowAllStudents] = useState(false)
	const [showAllParents, setShowAllParents] = useState(false)
	const [messageText, setMessageText] = useState('')
	const [showToast, setShowToast] = useState(false)
	const [isSending, setIsSending] = useState(false)
	const [sendError, setSendError] = useState(null)

	useEffect(() => {
		if (!isOpen) return
		cursorOffsetRef.current = null
		const list = Array.isArray(selectedStudents) ? [...selectedStudents] : []
		setLocalStudents(list)
		setAddParents(false)
		setShowAllStudents(false)
		setShowAllParents(false)
		setMessageText(typeof initialMessage === 'string' ? initialMessage : '')
		setSendError(null)
	}, [isOpen, selectedStudents, initialMessage])

	const visibleStudents = useMemo(
		() => (showAllStudents ? localStudents : localStudents.slice(0, MAX_VISIBLE)),
		[showAllStudents, localStudents],
	)
	const visibleParents = useMemo(
		() => (showAllParents ? selectedParents : selectedParents.slice(0, MAX_VISIBLE)),
		[showAllParents, selectedParents],
	)
	const hiddenStudentsCount = localStudents.length > MAX_VISIBLE ? localStudents.length - MAX_VISIBLE : 0
	const hiddenParentsCount = selectedParents.length > MAX_VISIBLE ? selectedParents.length - MAX_VISIBLE : 0

	const toggleShowAllStudents = useCallback(() => setShowAllStudents(prev => !prev), [])
	const toggleShowAllParents = useCallback(() => setShowAllParents(prev => !prev), [])

	const removeStudent = useCallback(id => {
		setLocalStudents(prev => prev.filter(s => String(s?.id ?? s?.studentId ?? '') !== String(id)))
	}, [])

	const insertStudentName = useCallback(() => {
		setMessageText(prev => {
			const pos = cursorOffsetRef.current
			const newStr =
				typeof pos === 'number' && pos >= 0
					? prev.slice(0, pos) + PLACEHOLDER_STUDENT + prev.slice(pos)
					: prev + PLACEHOLDER_STUDENT
			cursorOffsetRef.current = typeof pos === 'number' && pos >= 0 ? pos + PLACEHOLDER_STUDENT.length : prev.length + PLACEHOLDER_STUDENT.length
			return newStr
		})
	}, [])

	const insertParentName = useCallback(() => {
		setMessageText(prev => {
			const pos = cursorOffsetRef.current
			const newStr =
				typeof pos === 'number' && pos >= 0
					? prev.slice(0, pos) + PLACEHOLDER_PARENT + prev.slice(pos)
					: prev + PLACEHOLDER_PARENT
			cursorOffsetRef.current = typeof pos === 'number' && pos >= 0 ? pos + PLACEHOLDER_PARENT.length : prev.length + PLACEHOLDER_PARENT.length
			return newStr
		})
	}, [])

	const close = useCallback(() => onClose?.(), [onClose])

	const onSendMessage = useCallback(async () => {
		const studentIds = localStudents
			.map(s => s.id ?? s.student_id ?? s.studentId)
			.filter(Boolean)
			.map(String)
		if (!messageText.trim() || studentIds.length === 0) {
			setSendError('Please add a message and at least one recipient.')
			return
		}
		setSendError(null)
		setIsSending(true)
		try {
			if (typeof customSend === 'function') {
				await customSend({ message: messageText.trim(), studentIds })
			} else {
				await sendMessagesToStudents({
					message: messageText.trim(),
					student_ids: studentIds,
				})
			}
			await onAfterSendSuccess?.()
			close()
			setShowToast(true)
		} catch (err) {
			const msg = err?.response?.data?.error || err?.message || 'Failed to send messages.'
			setSendError(msg)
		} finally {
			setIsSending(false)
		}
	}, [messageText, localStudents, close, customSend, onAfterSendSuccess])

	const footerActions = useMemo(
		() => [
			{
				id: 'send',
				onClick: onSendMessage,
				variant: 'primary',
				text: isSending ? 'Sending…' : 'Send Now',
				disabled: isSending,
			},
		],
		[onSendMessage, isSending],
	)

	return {
		cursorOffsetRef,
		messageEditorRef,
		successToastMessage,
		localStudents,
		visibleStudents,
		hiddenStudentsCount,
		showAllStudents,
		toggleShowAllStudents,
		visibleParents,
		hiddenParentsCount,
		showAllParents,
		toggleShowAllParents,
		removeStudent,
		addParents,
		setAddParents,
		messageText,
		setMessageText,
		insertStudentName,
		insertParentName,
		close,
		footerActions,
		showToast,
		closeToast: () => setShowToast(false),
		isSending,
		sendError,
		setSendError,
		placeholderStudent: PLACEHOLDER_STUDENT,
		placeholderParent: PLACEHOLDER_PARENT,
	}
}
